package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

enum class ToastKind(val cls: String) {
    Info("wl-toast-info"),
    Success("wl-toast-success"),
    Error("wl-toast-error"),
}

data class ToastMessage(val text: String, val kind: ToastKind)

/**
 * Process-wide toast holder. Compose state, so any composable that reads
 * [current] re-renders when a new toast is shown. [show] auto-dismisses
 * after [defaultDurationMs] (configurable per call), cancelling any
 * in-flight dismissal so a fresh toast fully replaces an older one.
 */
object Toaster {
    var current: ToastMessage? by mutableStateOf(null)
        private set
    private var dismissJob: Job? = null
    private const val defaultDurationMs: Long = 3500
    private val scope = MainScope()

    fun show(text: String, kind: ToastKind = ToastKind.Info, durationMs: Long = defaultDurationMs) {
        if (text.isBlank()) return
        current = ToastMessage(text, kind)
        dismissJob?.cancel()
        dismissJob = scope.launch {
            delay(durationMs)
            current = null
        }
    }

    fun dismiss() {
        dismissJob?.cancel()
        current = null
    }
}

/**
 * Renders the active toast (if any) as a fixed-positioned bubble. Place
 * once at the top level of the App composable. Layered above `<md-dialog>`
 * via z-index 20000 so it's still visible if shown from inside a modal.
 */
@Composable
fun ToastHost() {
    val msg = Toaster.current ?: return
    Div({
        classes("wl-toast", msg.kind.cls)
    }) { Text(msg.text) }
}
